import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Circle, AlertTriangle, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import SectionCard from './SectionCard';

const FLOW_LABEL = {
  no_job: 'No open visit',
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  progress: 'In progress',
  completed: 'Completed',
};

const HEALTH_CLASS = {
  normal: 'text-gray-600',
  urgent: 'text-amber-700',
  overdue: 'text-red-700',
};

function StateIcon({ state }) {
  if (state === 'done') return <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />;
  if (state === 'waiting') return <Clock className="w-4 h-4 text-sky-600 shrink-0" />;
  return <Circle className="w-4 h-4 text-gray-300 shrink-0" />;
}

function stateText(state) {
  if (state === 'done') return 'Done';
  if (state === 'waiting') return 'Waiting';
  return 'Pending';
}

/**
 * External SS progress: mail → confirm → reminder → work → paperwork.
 * Loads GET /partner-companies/:partnerId/buildings/:buildingId/ss/:ssCode/progress
 */
export default function SsExternalProgressPanel({
  partnerId,
  buildingId,
  ssCode,
  onClose,
  compact = false,
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['partner-ss-progress', partnerId, buildingId, ssCode],
    queryFn: async () =>
      (
        await api.get(
          `/partner-companies/${partnerId}/buildings/${buildingId}/ss/${encodeURIComponent(ssCode)}/progress`
        )
      ).data?.data,
    enabled: !!partnerId && !!buildingId && !!ssCode,
  });

  if (isLoading) {
    return <p className="p-4 text-sm text-gray-500">Loading progress…</p>;
  }
  if (isError || !data) {
    return (
      <p className="p-4 text-sm text-red-600">
        {error?.response?.data?.error || 'Could not load progress'}
      </p>
    );
  }

  const timeline = data.timeline || [];
  const health = data.health || 'normal';

  return (
    <SectionCard
      title={`${data.ss?.ss_code || ssCode} · ${data.building?.name || ''}`}
      className={compact ? '' : 'mb-0'}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-2.5 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              Back
            </button>
          ) : null}
          {data.job?.id ? (
            <Link
              to={`/jobs/${data.job.id}`}
              className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
            >
              Open job <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
          <span>
            Partner: <strong>{data.partner?.name}</strong>
          </span>
          <span>
            Flow:{' '}
            <strong>{FLOW_LABEL[data.flow] || data.flow}</strong>
          </span>
          <span className={HEALTH_CLASS[health] || HEALTH_CLASS.normal}>
            {health === 'overdue' ? (
              <span className="inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Overdue
              </span>
            ) : health === 'urgent' ? (
              'Due soon'
            ) : (
              'On track'
            )}
          </span>
          {data.job?.due_date ? <span>Due {data.job.due_date}</span> : null}
          {data.job?.scheduled_date ? <span>Visit {data.job.scheduled_date}</span> : null}
        </div>
        {data.ss?.assignee_email ? (
          <p className="text-xs text-gray-500">Mail to: {data.ss.assignee_email}</p>
        ) : null}

        <ol className="space-y-2">
          {timeline.map((step) => (
            <li
              key={step.key}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
                step.state === 'waiting'
                  ? 'border-sky-200 bg-sky-50/80'
                  : step.state === 'done'
                    ? 'border-emerald-100 bg-white'
                    : 'border-gray-100 bg-white/70'
              }`}
            >
              <StateIcon state={step.state} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-gray-900">{step.label}</p>
                  <span className="text-xs text-gray-500">{stateText(step.state)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  {step.at || '—'}
                  {step.detail ? ` · ${step.detail}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {!data.job ? (
          <p className="text-xs text-gray-500">
            No inspection job for this period yet. Progress appears when a visit is opened or
            notified.
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
}
